// Rejestr akcji w formacie uzgodnionym z klientem (SR-014, wzór z 31.07.2026, plik „Format CSV”).
// Czyste funkcje: parsowanie schowka z Excela, daty DD.MM.RRRR, tak/nie, status, walidacja wiersza.
import { ActionStatus } from "../../../types/reportActions";

export type Category = { id: number; value: string };

// Wiersz edytowany w tabeli. Daty jako "YYYY-MM-DD" (bez strefy czasowej), puste = null.
export type ActionLogRow = {
  key: string; // id z API albo tymczasowy klucz nowego wiersza
  id?: string;
  participantNumber: string;
  visitNumber: string;
  categoryId: number | null;
  deviationDescription: string;
  identificationDate: string | null;
  closedDate: string | null;
  status: ActionStatus;
  actionsTaken: string;
  responsiblePerson: string;
  potentialDeviation: boolean;
  potentialSeriousFinding: boolean;
  seriousBreach: boolean;
  // tylko rejestr ośrodka: oznaczenie „do weryfikacji” z migracji (nie jest kolumną formatu)
  needsReview?: boolean;
  reviewNote?: string | null;
};

export type RowField = Exclude<keyof ActionLogRow, "key" | "id" | "needsReview" | "reviewNote">;
export type RowErrors = Partial<Record<RowField, string>>;

// Kolejność kolumn jak w arkuszu klienta (bez „lp.”, które jest numerem wiersza)
export const FORMAT_COLUMNS: { field: RowField; header: string }[] = [
  { field: "participantNumber", header: "Nr Uczestnika" },
  { field: "visitNumber", header: "Numer wizyty" },
  { field: "categoryId", header: "Czego dotyczy (ICF/ISF/eCRF/IMP)" },
  { field: "deviationDescription", header: "Opis" },
  { field: "identificationDate", header: "Data identyfikacji" },
  { field: "closedDate", header: "Data zamknięcia" },
  { field: "status", header: "Status (otwarte/zamknięte)" },
  { field: "actionsTaken", header: "Podjęte działania/Follow Up" },
  { field: "responsiblePerson", header: "Osoba odpowiedzialna w ośrodku" },
  { field: "potentialDeviation", header: "PD (potencjalne)" },
  { field: "potentialSeriousFinding", header: "Poważne znalezisko (potencjalne)" },
  { field: "seriousBreach", header: "Serious Breach" },
];

export const emptyRow = (key: string, today: string): ActionLogRow => ({
  key,
  participantNumber: "",
  visitNumber: "",
  categoryId: null,
  deviationDescription: "",
  identificationDate: today,
  closedDate: null,
  status: ActionStatus.Open,
  actionsTaken: "",
  responsiblePerson: "",
  potentialDeviation: false,
  potentialSeriousFinding: false,
  seriousBreach: false,
});

/* ----- daty ----- */

export const toIsoDate = (d: Date): string =>
  `${String(d.getFullYear()).padStart(4, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const validDate = (y: number, m: number, d: number): string | null => {
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  return date.getMonth() === m - 1 && date.getDate() === d ? toIsoDate(date) : null;
};

// Akceptuje DD.MM.RRRR (też z „/” lub „-”, jedno-cyfrowe dzień/miesiąc), RRRR-MM-DD oraz
// liczbę dni Excela (gdy komórka daty skopiowała się jako liczba). Zwraca "YYYY-MM-DD" albo null.
export const parseDatePl = (raw: string | null | undefined): string | null => {
  const s = (raw ?? "").trim();
  if (!s) return null;
  let m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) return validDate(+m[3], +m[2], +m[1]);
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:T.*)?$/);
  if (m) return validDate(+m[1], +m[2], +m[3]);
  if (/^\d{5}$/.test(s)) {
    const excelEpoch = new Date(1899, 11, 30);
    excelEpoch.setDate(excelEpoch.getDate() + Number(s));
    return toIsoDate(excelEpoch);
  }
  return null;
};

export const formatDatePl = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const [y, m, d] = iso.split("T")[0].split("-");
  return y && m && d ? `${d}.${m}.${y}` : "";
};

/* ----- tak/nie, status, kategoria ----- */

const norm = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ł/g, "l");

export const parseYesNo = (raw: string): boolean | null => {
  const s = norm(raw);
  if (!s) return false;
  if (["tak", "t", "yes", "y", "x", "1", "true"].includes(s)) return true;
  if (["nie", "n", "no", "0", "false", "-"].includes(s)) return false;
  return null;
};

export const parseStatus = (raw: string): ActionStatus | null => {
  const s = norm(raw);
  if (!s) return null;
  if (s.startsWith("otw") || s === "open") return ActionStatus.Open;
  if (s.startsWith("zamk") || s === "closed") return ActionStatus.Closed;
  return null;
};

// Skróty z arkusza klienta („Czego dotyczy (ICF/ISF/eCRF/IMP)”) → nazwy kategorii na produkcji (stan 25.10.2026)
const CATEGORY_ALIASES: Record<string, string> = {
  icf: "swiadoma zgoda",
  isf: "investigator site file",
  ecrf: "przeglad ecrf",
  imp: "produkt badany",
  pd: "odchylenia od protokolu",
};

// Nazwy kategorii mają czasem dwukropek i spację na końcu („ODCHYLENIA OD PROTOKOŁU: ”)
const normCategory = (s: string) => norm(s).replace(/[\s:]+$/, "");

export const findCategory = (raw: string, categories: Category[]): number | null => {
  const s = normCategory(raw);
  if (!s) return null;
  const byName = (name: string) => categories.find((c) => normCategory(c.value) === name);
  const match = byName(s) ?? (CATEGORY_ALIASES[s] ? byName(CATEGORY_ALIASES[s]) : undefined);
  return match ? match.id : null;
};

/* ----- walidacja (odpowiada ograniczeniom akcji w bazie) ----- */

// Rejestr akcji raportu wymaga „Podjętych działań” przy zamknięciu (ograniczenie w bazie). Rejestr ośrodka nie,
// bo trafiły do niego też znaleziska, które tego pola nie miały.
export type ValidationRules = { requireActionsOnClose: boolean };
export const REPORT_ACTION_RULES: ValidationRules = { requireActionsOnClose: true };
export const SITE_ISSUE_RULES: ValidationRules = { requireActionsOnClose: false };

export const validateRow = (r: ActionLogRow, rules: ValidationRules = REPORT_ACTION_RULES): RowErrors => {
  const e: RowErrors = {};
  if (!r.deviationDescription.trim()) e.deviationDescription = "Opis jest wymagany";
  if (!r.identificationDate) e.identificationDate = "Podaj datę identyfikacji (DD.MM.RRRR)";
  if (r.status === ActionStatus.Closed) {
    if (!r.closedDate) e.closedDate = "Zamknięta akcja wymaga daty zamknięcia";
    if (rules.requireActionsOnClose && !r.actionsTaken.trim()) e.actionsTaken = "Zamknięta akcja wymaga opisu podjętych działań";
  }
  if (r.status === ActionStatus.Open && r.closedDate) e.closedDate = "Otwarta akcja nie ma daty zamknięcia";
  if (r.closedDate && r.identificationDate && r.closedDate < r.identificationDate)
    e.closedDate = "Data zamknięcia jest wcześniejsza niż data identyfikacji";
  if (r.participantNumber.length > 400) e.participantNumber = "Maksymalnie 400 znaków";
  return e;
};

/* ----- tabele z Excela: schowek, CSV, xlsx ----- */

// Excel kopiuje zaznaczenie jako TSV (kolumny tabulatorem, wiersze nową linią), CSV ma ; albo ,.
// Komórka z separatorem, nową linią albo cudzysłowem jest w cudzysłowie z podwojonym " w środku.
export const parseDelimited = (text: string, delimiter = "\t"): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  const src = text.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") inQuotes = true;
    else if (ch === delimiter) { row.push(cell); cell = ""; }
    else if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  if (cell !== "" || row.length > 0) { row.push(cell); rows.push(row); }
  return rows;
};

export const parseClipboardTable = (text: string): string[][] =>
  parseDelimited(text, "\t").filter((r) => r.some((c) => c.trim() !== ""));

// Separator CSV: ten z ; , i tabulatora, który najczęściej występuje w pierwszych liniach
export const detectCsvDelimiter = (text: string): string => {
  const sample = text.split(/\r?\n/).slice(0, 5).join("\n");
  const counts = [";", ",", "\t"].map((d) => ({ d, n: sample.split(d).length - 1 }));
  return counts.sort((a, b) => b.n - a.n)[0].n > 0 ? counts[0].d : ";";
};

export type ImportIssue = { level: "error" | "warning" | "info"; message: string };
export type PastedRow = { row: ActionLogRow; errors: RowErrors; source: string[]; line?: number };
export type ParsedImport = { rows: PastedRow[]; issues: ImportIssue[] };

// Dopasowanie nagłówka do kolumny formatu po początku nazwy (bez wielkości liter i polskich znaków),
// np. „Status (otwarte/zamknięte)”, „Status”, „Nr uczestnika” → kolumna formatu
const HEADER_KEYS: { field: RowField | "lp"; key: string }[] = [
  { field: "lp", key: "lp" },
  ...FORMAT_COLUMNS.map((c) => ({ field: c.field, key: norm(c.header).split(" (")[0].split("/")[0].trim() })),
  { field: "participantNumber", key: "numer uczestnika" },
  { field: "visitNumber", key: "nr wizyty" },
  { field: "actionsTaken", key: "podjete dzialania" },
  { field: "actionsTaken", key: "follow up" },
  { field: "potentialDeviation", key: "pd" },
  { field: "responsiblePerson", key: "osoba odpowiedzialna" },
  { field: "potentialSeriousFinding", key: "powazne znalezisko" },
];
const matchHeader = (cell: string): RowField | "lp" | null => {
  const v = norm(cell).replace(/\s+/g, " ").replace(/[\s.:]+$/, "");
  if (!v) return null;
  const hit = HEADER_KEYS.find((h) => v === h.key || v.startsWith(h.key + " ") || v.startsWith(h.key + "(") || v.startsWith(h.key + "/"));
  return hit ? hit.field : null;
};
const isHeaderRow = (cells: string[]) => cells.filter((c) => matchHeader(c) !== null).length >= 3;

// Wiersz z opisem kolumn ze wzoru klienta („DD.MM.RRRR”, „Predefiniowane: …”, „Obszerna część…”)
const isFormatDescriptionRow = (cells: string[]) =>
  cells.filter((c) => /dd\.mm\.rrrr|^predefiniowane|^obszerna czesc|^do wpisania/.test(norm(c))).length >= 2;

const REQUIRED: RowField[] = ["deviationDescription", "identificationDate"];
const headerName = (f: RowField) => FORMAT_COLUMNS.find((c) => c.field === f)!.header;

// Zamienia tabelę (wklejoną, z CSV albo z xlsx) na wiersze rejestru i listę problemów z formatem.
// `firstLine` = numer wiersza w pliku odpowiadający table[0] (do komunikatów „wiersz N”).
export const rowsFromTable = (
  table: string[][],
  categories: Category[],
  newKey: () => string,
  today: string,
  firstLine = 1,
  rules: ValidationRules = REPORT_ACTION_RULES
): ParsedImport => {
  const issues: ImportIssue[] = [];
  const lines = table
    .map((cells, i) => ({ cells: cells.map((c) => (c ?? "").toString()), line: firstLine + i }))
    .filter((r) => r.cells.some((c) => c.trim() !== ""));
  if (lines.length === 0) return { rows: [], issues: [{ level: "error", message: "Brak danych: plik albo schowek jest pusty." }] };

  // Nagłówek szukany w pierwszych 10 niepustych wierszach (nad nim bywa tytuł arkusza)
  const headerIdx = lines.slice(0, 10).findIndex((r) => isHeaderRow(r.cells));
  let mapping: (RowField | null)[];
  let data = lines;
  if (headerIdx >= 0) {
    const header = lines[headerIdx];
    if (headerIdx > 0) issues.push({ level: "info", message: `Pominięto ${headerIdx} ${headerIdx === 1 ? "wiersz" : "wiersze"} nad nagłówkiem (tytuł arkusza).` });
    const seen = new Set<RowField>();
    mapping = header.cells.map((c) => {
      const f = matchHeader(c);
      if (f === null) {
        if (c.trim()) issues.push({ level: "warning", message: `Nieznana kolumna „${c.trim()}” (wiersz ${header.line}) zostanie pominięta.` });
        return null;
      }
      if (f === "lp") return null;
      if (seen.has(f)) {
        issues.push({ level: "warning", message: `Kolumna „${c.trim()}” występuje drugi raz; brana jest pierwsza.` });
        return null;
      }
      seen.add(f);
      return f;
    });
    REQUIRED.filter((f) => !seen.has(f)).forEach((f) =>
      issues.push({ level: "error", message: `Brak wymaganej kolumny „${headerName(f)}”. Sprawdź nagłówek (wiersz ${header.line}) albo użyj wzoru.` }));
    FORMAT_COLUMNS.filter((c) => !REQUIRED.includes(c.field) && !seen.has(c.field)).forEach((c) =>
      issues.push({ level: "warning", message: `Brak kolumny „${c.header}”; to pole zostanie puste.` }));
    data = lines.slice(headerIdx + 1);
  } else {
    // Bez nagłówka: kolumny w kolejności wzoru (12 kolumn albo 13 z „lp.” na początku)
    const width = Math.max(...lines.map((r) => r.cells.length));
    if (width < 4) {
      return { rows: [], issues: [{ level: "error", message: `Za mało kolumn (${width}). Oczekiwano 12 kolumn w kolejności wzoru (13 z „lp.”) albo wiersza nagłówka z nazwami kolumn.` }] };
    }
    const offset = width >= FORMAT_COLUMNS.length + 1 ? 1 : 0;
    mapping = [...Array(offset).fill(null), ...FORMAT_COLUMNS.map((c) => c.field)];
    issues.push({ level: "info", message: `Nie znaleziono wiersza nagłówka: kolumny przyjęte w kolejności wzoru${offset ? " (pierwsza kolumna = lp.)" : ""}.` });
    if (width !== FORMAT_COLUMNS.length && width !== FORMAT_COLUMNS.length + 1) {
      issues.push({ level: "warning", message: `Liczba kolumn (${width}) nie zgadza się ze wzorem (12 albo 13). Sprawdź, czy dane trafiły do właściwych kolumn.` });
    }
  }

  const skipped = data.filter((r) => isFormatDescriptionRow(r.cells));
  if (skipped.length) issues.push({ level: "info", message: `Pominięto wiersz z opisem kolumn ze wzoru (wiersz ${skipped.map((r) => r.line).join(", ")}).` });
  data = data.filter((r) => !isFormatDescriptionRow(r.cells));
  if (data.length === 0 && !issues.some((i) => i.level === "error")) {
    issues.push({ level: "error", message: "Pod nagłówkiem nie ma żadnych pozycji do zaimportowania." });
  }

  const rows = data.map(({ cells, line }) => {
    const get = (field: RowField) => {
      const i = mapping.indexOf(field);
      return i >= 0 ? (cells[i] ?? "").trim() : "";
    };
    const row = emptyRow(newKey(), today);
    const errors: RowErrors = {};

    row.participantNumber = get("participantNumber");
    row.visitNumber = get("visitNumber");
    if (get("categoryId")) {
      row.categoryId = findCategory(get("categoryId"), categories);
      if (row.categoryId === null) errors.categoryId = `Nieznana kategoria „${get("categoryId")}”. Dozwolone: ${categories.map((c) => c.value.trim()).join(", ")} (albo skróty ICF/ISF/eCRF/IMP)`;
    }
    row.deviationDescription = get("deviationDescription");
    row.identificationDate = parseDatePl(get("identificationDate"));
    if (get("identificationDate") && !row.identificationDate) errors.identificationDate = `Nie rozpoznano daty „${get("identificationDate")}” (oczekiwano DD.MM.RRRR)`;
    row.closedDate = parseDatePl(get("closedDate"));
    if (get("closedDate") && !row.closedDate) errors.closedDate = `Nie rozpoznano daty „${get("closedDate")}” (oczekiwano DD.MM.RRRR)`;
    const status = parseStatus(get("status"));
    if (get("status") && status === null) errors.status = `Nieznany status „${get("status")}” (dozwolone: otwarte, zamknięte)`;
    row.status = status ?? (row.closedDate ? ActionStatus.Closed : ActionStatus.Open);
    row.actionsTaken = get("actionsTaken");
    row.responsiblePerson = get("responsiblePerson");
    (["potentialDeviation", "potentialSeriousFinding", "seriousBreach"] as const).forEach((field) => {
      const v = parseYesNo(get(field));
      if (v === null) errors[field] = `Oczekiwano tak/nie, jest „${get(field)}”`;
      row[field] = v ?? false;
    });

    return { row, errors: { ...validateRow(row, rules), ...errors }, source: cells, line };
  });

  return { rows, issues };
};

export const rowsFromClipboard = (
  text: string, categories: Category[], newKey: () => string, today: string, rules: ValidationRules = REPORT_ACTION_RULES
): ParsedImport => rowsFromTable(parseDelimited(text, "\t"), categories, newKey, today, 1, rules);
